import { useState, useCallback } from 'react';
import { getUrl, getCleanedUrlParams } from '@utils';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { addMonths, subMonths } from 'date-fns';

import { boardsServices } from '@services';
import { BOARDS_ID } from '@constants';

import { QUERY_KEYS } from 'constants/queryKeys';
import { URLS } from 'layouts/DrawerLayout/constants';
import {
  LOGISTICS_DRIVERS_PAGE_SIZE,
  LOGISTICS_DRIVERS_DEFAULT_ORDER,
} from 'components/drivers/constants';
import { useAssignees } from 'hooks/common';
import {
  getLogisticsPayload,
  getLogisticsSortingParams,
} from '../logistics/utils';
import {
  getMaxColumns,
  handleLogisticsDriversData,
  calcTotalDrivers,
} from './utils';

const useLogisticsDriversView = () => {
  const { query, push } = useRouter();
  const {
    drivers: cachedDrivers,
    isLoading: isCachedDriversLoading,
    isFetched: isCachedDriversFetched,
  } = useAssignees();

  const [date, setDate] = useState(new Date());

  const {
    page = 1,
    pageSize = LOGISTICS_DRIVERS_PAGE_SIZE,
    order = LOGISTICS_DRIVERS_DEFAULT_ORDER,
    orderBy = 'scheduledDate',
    ...filters
  } = query;

  const logisticsResponse = useQuery(
    [QUERY_KEYS.GET_LOGISTICS_DRIVERS_KEY, { ...query }],
    () => {
      const params = { page, pageSize, order, orderBy };

      const cleanedParams = getCleanedUrlParams(params);

      const sortingParams = getLogisticsSortingParams(cleanedParams);

      const payload = getLogisticsPayload(filters);

      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.LOGISTICS,
        params: sortingParams,
        payload,
      });
    },
    { refetchOnMount: true, enabled: isCachedDriversFetched }
  );

  const drivers = handleLogisticsDriversData({
    logistics: logisticsResponse?.data?.data,
    assignees: cachedDrivers,
    date,
  });

  const maxColumns = getMaxColumns(drivers);

  const total = calcTotalDrivers(drivers);

  const loading = isCachedDriversLoading || logisticsResponse?.isLoading;

  const getToday = useCallback(() => {
    setDate(new Date());
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFrontDate = useCallback(() => {
    setDate(addMonths(date, 1));
  }, [date]);

  const getBackDate = useCallback(() => {
    setDate(subMonths(date, 1));
  }, [date]);

  const setCustomDate = (customDate) => {
    setDate(customDate);
  };

  const handlePageChange = (newPage) => {
    const currentPage = query.page || '1';
    if (newPage !== currentPage) {
      push(getUrl({ ...query, page: newPage }, URLS.drivers));
    }
  };

  return {
    total,
    page,
    pageSize,
    date,
    loading,
    drivers,
    maxColumns,
    getToday,
    getFrontDate,
    getBackDate,
    setCustomDate,
    nextPage: handlePageChange,
  };
};

export default useLogisticsDriversView;
